// Swap Curve line chart: TenorDate (X, time axis) vs ZeroRate (Y, as %). Rendered through the
// shared CubeAgChart wrapper, so its colour/line/marker styling comes from cubeChartTheme.
//
// Plots the curve(s) fetched from the back end (/Curves/ByBlocks) for the dropped trade/block.
// One line series is drawn per curve; when more than one curve is returned they are OVERLAID and
// the legend is enabled (click a legend entry to hide/show that line). Distinct colours come from
// the shared theme palette. When there are no curves it renders an empty chart. OP trades may
// also pass indicative mids, drawn as an "Indicative Mid" scatter series per axis - each marker at
// its own swap tenor (leg `maturity`), falling back to the earliest tenor when that's absent, and
// the quoting counterparties' rates (LQC-1899) as one scatter series per CP, each dot on its real
// tenor, with the leading counterparty drawn last so it survives an overlap.
import CubeAgChart from "@/components/UI/charts/CubeAgChart";
import {
  getCubeChartOverflowColour,
  getCubeChartOverlayPalette,
  getCubeChartPalette,
  getCubeChartSurface,
} from "@/components/UI/charts/cubeChartTheme";
import type { AgChartOptions } from "ag-charts-community";
import { useTheme } from "next-themes";
import { useMemo } from "react";
import { CounterpartyQuoteMarker } from "./helper/counterpartyQuoteHelpers";
import { buildCounterpartyQuoteSeries } from "./helper/counterpartyQuoteSeries";
import {
  formatQuoteWithUnit,
  IndicativeMidMarker,
  interpolateZeroRateAtDate,
  RATE_AXIS_KEYS,
  securityLabel,
  SPREAD_AXIS_KEYS,
  spreadYKey,
  SwapCurvePoint,
  SwapCurveYAxis,
  yAxisForNotation,
} from "./helper/swapCurveHelpers";

// One overlaid curve: a display name (legend label + series name) and its plotted points.
export interface SwapCurveSeries {
  name: string;
  points: SwapCurvePoint[];
}

interface ISwapCurveChart {
  // One or more curves to overlay. Each becomes its own line series; the legend is enabled
  // (and clickable to hide/show a line) whenever there is more than one.
  curves?: SwapCurveSeries[];
  // OP-only reference markers: each swap block's indicative mid, plotted as a single
  // "Indicative Mid" scatter series at each leg's tenor. Absent/empty for block drops.
  indicativeMids?: IndicativeMidMarker[];
  // OP-only: the rate each quoting counterparty put on every swap block. One scatter series per
  // CP, each dot on its real tenor; the leading CP is drawn last so an overlap keeps it visible.
  counterpartyQuotes?: CounterpartyQuoteMarker[];
}

// Tooltip for the indicative-mid markers: the rate shares the title row with the label and sits
// to its right, matching the counterparty tooltips so every price on the chart reads the same way.
export const indicativeMidTooltipRenderer = (params: any) =>
  `<div class="ag-chart-tooltip-title" style="display:flex;align-items:baseline;justify-content:space-between;gap:24px;white-space:nowrap;padding:6px 10px;"><span>Indicative Mid</span><span>${formatQuoteWithUnit(params.datum?.mid, params.datum?.notation)}</span></div><div class="ag-chart-tooltip-content" style="max-width:300px;padding:6px 10px;">${securityLabel(params.datum)}</div>`;

const SwapCurveChart = ({
  curves,
  indicativeMids,
  counterpartyQuotes,
}: ISwapCurveChart) => {
  const { theme } = useTheme();

  const options = useMemo<AgChartOptions | any>(() => {
    // Defaulted inside the memo: a `?? []` outside it is a new array every render, which would
    // rebuild the whole chart on any parent re-render.
    const seriesList = curves ?? [];
    const midMarkers = indicativeMids ?? [];
    const cpQuotes = counterpartyQuotes ?? [];

    // Same palette the theme assigns to series in order (series[i] -> palette[i]), so each
    // tooltip row's colour dot matches its line.
    const palette = getCubeChartPalette(theme);
    // The quoted prices layered ON the curve read as a group, and take the high-contrast overlay
    // palette rather than the next series colours: the series palette is tuned for area fills and
    // is too low-chroma to carry small marks. Assigned in sequence - mid first, then each CP.
    const overlayPalette = getCubeChartOverlayPalette(theme);
    const surface = getCubeChartSurface(theme);

    // ONE combined tooltip regardless of which line is hovered: list every overlaid curve's
    // rate at the hovered date. The hovered curve resolves to its exact point; the others are
    // interpolated so both values always show even when the curves don't share tenor dates.
    // Shared by every series, so two boxes can never overlap with conflicting content.
    //
    // AG Charts v11's structured result ({title, data:[{label,value}]}) is plain text only, so
    // we return a raw HTML STRING instead — the only way to keep colour dots, right-aligned
    // values and a custom width.
    const tooltipRenderer = (params: any) => {
      const date =
        params.datum?.tenorDate instanceof Date ? params.datum.tenorDate : null;
      const title = date ? String(date.getFullYear()) : "";
      const rows = seriesList
        .map((curve, index) => {
          const rate = date
            ? interpolateZeroRateAtDate(curve.points, date)
            : undefined;
          if (typeof rate !== "number") return "";
          const color = palette[index % palette.length];
          const dot = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:6px;flex:0 0 auto;"></span>`;
          // nowrap on each row so long curve names stay on one line; the wrapper's min-width
          // gives the box room and keeps the value column aligned to the right.
          return `<div style="display:flex;align-items:center;gap:16px;justify-content:space-between;white-space:nowrap;"><span style="display:flex;align-items:center;">${dot}${curve.name}</span><span>${rate.toFixed(3)}%</span></div>`;
        })
        .join("");
      // Raw HTML string: own title + content wrappers, widened so names/values never clip.
      return `<div class="ag-chart-tooltip-title" style="padding:6px 10px;">${title}</div><div class="ag-chart-tooltip-content" style="min-width:360px;padding:6px 10px;">${rows}</div>`;
    };

    const lineSeries = seriesList.map((curve) => ({
      type: "line",
      // Each series carries its own data so overlaid curves keep independent point sets
      // (real markers stay on real data points; other curves are interpolated for the tooltip).
      data: curve.points,
      xKey: "tenorDate",
      yKey: "zeroRatePercent",
      yName: curve.name,
      tooltip: { renderer: tooltipRenderer },
    }));

    // Earliest curve tenor, the fallback x for any mid or CP quote whose leg has no maturity.
    const earliest = seriesList
      .map((c) => c.points[0]?.tenorDate)
      .filter((d): d is Date => d instanceof Date)
      .sort((a, b) => a.getTime() - b.getTime())[0];

    // Indicative-mid scatter: one marker per swap block. Each sits at its leg's own tenor
    // (`maturity`); markers without one fall back to the earliest curve tenor. Markers with no
    // anchor at all (no maturity and no curves) are dropped.
    const anchoredMids = midMarkers
      .map((m) => ({ ...m, tenorDate: m.tenorDate ?? earliest }))
      .filter(
        (m): m is IndicativeMidMarker & { tenorDate: Date } =>
          m.tenorDate instanceof Date,
      );

    // The indicative mid IS the curve's own level, so with a single curve it takes that curve's
    // colour rather than an overlay slot - which also frees slot 0 for a counterparty. With several
    // curves the /Curves/ByBlocks response carries no key tying a block to a curve, so guessing the
    // pairing would assert a relationship we cannot verify; it keeps a distinct colour instead.
    // A bps spread is never a curve's level, so it always takes the overlay colour.
    const midMatchesCurve = seriesList.length === 1;

    // LQC-2883: a bps basis spread on the Zero Rate axis read as a rate 100x its size (20.83 bps
    // drawn at 20.83%), so each mid goes on the axis of its own unit. Other units plot nowhere.
    const midAxes: SwapCurveYAxis[] = ["rate", "spread"];
    const midSeries = midAxes.flatMap((axis) => {
      const data = anchoredMids
        .filter((m) => yAxisForNotation(m.notation) === axis)
        .map((m) =>
          axis === "spread" ? { ...m, [spreadYKey("mid")]: m.mid } : m,
        );
      if (data.length === 0) return [];
      const usesCurveColour = axis === "rate" && midMatchesCurve;
      return [
        {
          type: "scatter",
          data,
          xKey: "tenorDate",
          yKey: axis === "spread" ? spreadYKey("mid") : "mid",
          yName: axis === "spread" ? "Indicative Mid (bps)" : "Indicative Mid",
          // Surface-coloured ring so the diamond lifts off the curve it sits on - it is
          // what keeps the marker readable when it shares the curve's colour.
          marker: {
            shape: "diamond",
            size: 11,
            fill: usesCurveColour ? palette[0] : overlayPalette[0],
            stroke: surface,
            strokeWidth: 2,
          },
          tooltip: { renderer: indicativeMidTooltipRenderer },
        },
      ];
    });

    // Counterparties take the overlay slots after the indicative mid, so no CP can share a colour
    // with another price already on the chart.
    const counterpartySeries = buildCounterpartyQuoteSeries({
      quotes: cpQuotes,
      palette: overlayPalette,
      // Only skip a slot when a mid actually used one: any spread mid, or a rate mid off the curve.
      paletteOffset: midSeries.some((m) => m.yKey !== "mid" || !midMatchesCurve)
        ? 1
        : 0,
      surface,
      overflowColour: getCubeChartOverflowColour(theme),
      fallbackTenor: earliest,
    });

    // The right-hand axis exists only when a bps price is on the chart, so % trades are unchanged.
    const hasSpreadSeries = [...midSeries, ...counterpartySeries].some((s) =>
      SPREAD_AXIS_KEYS.includes(s.yKey),
    );

    return {
      series: [...lineSeries, ...midSeries, ...counterpartySeries],
      axes: [
        {
          type: "time",
          position: "bottom",
          title: { text: "Tenor" },
          label: { format: "%Y" },
          // Bound the axis to the actual data extent so it doesn't round out to a "nice" year
          // (e.g. 2106) far past the last point, leaving a large empty gap on the right.
          nice: false,
        },
        {
          type: "number",
          position: "left",
          keys: RATE_AXIS_KEYS,
          title: { text: "Zero Rate" },
          label: {
            formatter: (params: any) => `${params.value.toFixed(2)}%`,
          },
        },
        // Scaled independently of Zero Rate: a spread is not a rate, so there is no shared zero
        // to pin it to, and its own range keeps a tight spread readable.
        ...(hasSpreadSeries
          ? [
              {
                type: "number",
                position: "right",
                keys: SPREAD_AXIS_KEYS,
                title: { text: "bps" },
                label: {
                  formatter: (params: any) => `${params.value.toFixed(1)} bps`,
                },
              },
            ]
          : []),
      ],
      // Enabled once there's more than one series to distinguish (multiple curves, or a
      // curve plus the indicative-mid / counterparty overlays).
      legend: {
        enabled:
          lineSeries.length + midSeries.length + counterpartySeries.length > 1,
      },
      // LQC-2831. Counterparty quotes can sit fractions of a basis point apart, which no amount
      // of marker styling separates - so let the user magnify instead. Scroll to zoom, drag to
      // pan, double-click to reset: AG Charts defaults, so it behaves like every other chart.
      zoom: {
        enabled: true,
        // Quotes cluster on the RATE, not the tenor, so the default x-only zoom misses the axis
        // that actually needs it.
        axes: "xy",
        // Default floor of 2 items stops the zoom before near-identical rates separate.
        minVisibleItems: 0,
        buttons: { enabled: true, visible: "hover" },
      },
      // The zoom module adds "Zoom to here" / "Pan to here" to the right-click menu. Both re-layout
      // the chart mid-gesture and broke the widget's height, so the menu is enumerated without
      // them. Listing items replaces "defaults" wholesale - there is no way to subtract one.
      contextMenu: {
        items: ["download", "toggle-series-visibility", "toggle-other-series"],
      },
    };
  }, [curves, indicativeMids, counterpartyQuotes, theme]);

  return <CubeAgChart options={options} testId="SwapCurveChartContainer" />;
};

export default SwapCurveChart;
